import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area 
} from "recharts";
import { 
  Calendar, Users, TrendingUp, MapPin, Vote, Award, Star, Sparkles, 
  Zap, Globe, History, Crown, User, ChevronRight, Clock, Flag,
  ArrowRight, CheckCircle, AlertCircle
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UMLSunIcon, CongressTreeIcon, MaoistHammerIcon, RSPBellIcon } from "../components/PartyIcons";
import { API } from "@/config";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1764140608148-80e010804af8?w=1920&q=80",
};

const LiveTicker = () => (
  <div className="bg-gradient-to-r from-gorkhali-red via-peace-blue to-gorkhali-red py-3 overflow-hidden">
    <motion.div className="flex whitespace-nowrap" animate={{ x: [0, -1500] }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
      {[...Array(4)].map((_, i) => (
        <span key={i} className="mx-8 text-white font-bebas tracking-wider text-sm">
          📊 NEPAL ELECTION HISTORY • 2008-2026 • PM SUCCESSION • ALL ELECTIONS • <span className="text-marigold">★</span>
        </span>
      ))}
    </motion.div>
  </div>
);

const Footer = () => (
  <footer className="bg-black py-16 relative overflow-hidden">
    <div className="absolute inset-0"><div className="absolute top-0 left-1/4 w-96 h-96 bg-gorkhali-red/5 rounded-full blur-3xl" /><div className="absolute bottom-0 right-1/4 w-96 h-96 bg-rsp-blue/5 rounded-full blur-3xl" /></div>
    <div className="max-w-6xl mx-auto px-4 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <motion.div className="w-12 h-12" animate={{ rotate: [0, 5, -5, 0] }} transition={{ duration: 4, repeat: Infinity }}>
              <svg viewBox="0 0 40 48" className="w-full h-full"><polygon points="0,0 40,16 0,32" fill="#D90429" /><polygon points="0,16 40,32 0,48" fill="#003049" /><circle cx="12" cy="12" r="4" fill="white" /><circle cx="12" cy="28" r="4" fill="white" /></svg>
            </motion.div>
            <div><h3 className="font-bebas text-2xl text-white tracking-wider">NEPALI BALLOT</h3><p className="text-gray-500 text-xs">Election Information Platform</p></div>
          </div>
          <p className="font-inter text-gray-400 text-sm mb-4">Accurate information about Nepal's democratic journey.</p>
          <motion.div className="inline-flex items-center gap-2 bg-gradient-to-r from-gorkhali-red/20 to-rsp-blue/20 px-4 py-2 rounded-full border border-white/10" animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 2, repeat: Infinity }}>
            <Sparkles className="text-marigold" size={16} /><span className="text-white font-bebas tracking-wider text-sm">BUILT BY TWO GEN Zs</span><Zap className="text-rsp-blue" size={16} />
          </motion.div>
        </div>
        <div><h4 className="font-bebas text-lg text-white tracking-wider mb-4">EXPLORE</h4><ul className="space-y-2 font-inter text-sm text-gray-400">{["Vote Now", "Live Results", "History", "Sources"].map(l => <li key={l}><a href="#" className="hover:text-marigold flex items-center gap-2"><Star size={12} className="text-marigold" />{l}</a></li>)}</ul></div>
      </div>
      <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-inter text-gray-500 text-sm">© 2026 Nepali Ballot</p>
        <div className="flex items-center gap-4"><motion.span className="text-2xl" animate={{ rotate: [0, 20, -20, 0] }} transition={{ duration: 2, repeat: Infinity }}>🇳🇵</motion.span><span className="text-gray-600 text-xs">Information Only</span></div>
      </div>
    </div>
  </footer>
);

// PM Card Component
const PMCard = ({ pm, index, isLast }) => {
  const tenureDays = pm.tenure_days || "Ongoing";
  const tenureYears = pm.tenure_days ? (pm.tenure_days / 365).toFixed(1) : null;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="relative"
    >
      {/* Connection Line */}
      {!isLast && (
        <div className="absolute left-6 top-16 w-0.5 h-full bg-gradient-to-b from-white/30 to-transparent" />
      )}
      
      <div className="flex gap-4">
        {/* Timeline dot */}
        <div className="flex-shrink-0 relative z-10">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center border-2"
            style={{ borderColor: pm.party_color, backgroundColor: `${pm.party_color}20` }}
          >
            <User size={20} style={{ color: pm.party_color }} />
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 pb-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="font-bebas text-lg text-white tracking-wider">
                  {pm.name}
                  {pm.term && <span className="text-gray-400 text-sm ml-2">(Term {pm.term})</span>}
                </h3>
                <p className="text-sm font-inter" style={{ color: pm.party_color }}>{pm.party}</p>
              </div>
              {pm.end_date === null && (
                <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-bebas">CURRENT</span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-4 text-xs text-gray-400 mb-2">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {pm.start_date} → {pm.end_date || "Present"}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {tenureYears ? `${tenureYears} years (${tenureDays} days)` : "Ongoing"}
              </span>
            </div>
            
            <p className="text-gray-400 text-sm font-inter">{pm.note}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Political Event Component
const TimelineEvent = ({ event, index }) => {
  const typeColors = {
    tragedy: "#EF4444",
    political: "#F59E0B",
    movement: "#10B981",
    peace: "#22C55E",
    constitution: "#3B82F6",
    historic: "#8B5CF6",
    election: "#EC4899",
    upcoming: "#06B6D4"
  };
  
  const typeIcons = {
    tragedy: AlertCircle,
    political: Flag,
    movement: Users,
    peace: CheckCircle,
    constitution: Globe,
    historic: Crown,
    election: Vote,
    upcoming: Calendar
  };
  
  const Icon = typeIcons[event.type] || Star;
  const color = typeColors[event.type] || "#6B7280";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex gap-4 group"
    >
      <div className="flex-shrink-0 text-right w-20">
        <span className="font-bebas text-2xl text-marigold">{event.year}</span>
        <p className="text-xs text-gray-500">BS {event.bs}</p>
      </div>
      
      <div className="relative">
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center border-2 group-hover:scale-110 transition-transform"
          style={{ borderColor: color, backgroundColor: `${color}20` }}
        >
          <Icon size={16} style={{ color }} />
        </div>
        <div className="absolute top-10 left-1/2 w-0.5 h-full -translate-x-1/2 bg-gradient-to-b from-white/20 to-transparent" />
      </div>
      
      <div className="flex-1 pb-6">
        <h4 className="font-bebas text-lg text-white tracking-wider">{event.event}</h4>
        <p className="text-gray-400 text-sm font-inter">{event.desc}</p>
      </div>
    </motion.div>
  );
};

// Turnout Trend Chart Data
const getTurnoutTrendData = (elections) => {
  return elections
    .filter(e => e.election_type.includes("Federal") || e.election_type.includes("Constituent"))
    .map(e => ({
      year: e.year,
      turnout: e.turnout_percentage,
      voters: (e.total_voters / 1000000).toFixed(1)
    }))
    .sort((a, b) => a.year - b.year);
};

const HistoryPage = () => {
  const [historicalData, setHistoricalData] = useState([]);
  const [pmSuccession, setPmSuccession] = useState([]);
  const [politicalTimeline, setPoliticalTimeline] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedElection, setSelectedElection] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [mainTab, setMainTab] = useState("elections");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [histRes, provRes, pmRes, timelineRes] = await Promise.all([
          axios.get(`${API}/historical`),
          axios.get(`${API}/provinces`),
          axios.get(`${API}/pm-succession`).catch(() => ({ data: { prime_ministers: [] } })),
          axios.get(`${API}/political-timeline`).catch(() => ({ data: { events: [] } }))
        ]);
        setHistoricalData(histRes.data);
        setProvinces(provRes.data);
        setPmSuccession(pmRes.data.prime_ministers || []);
        setPoliticalTimeline(timelineRes.data.events || []);
        if (histRes.data.length > 0) setSelectedElection(histRes.data[0]);
      } catch (e) { 
        console.error(e);
        toast.error("Failed to load data"); 
      }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-night-count">
      <motion.div className="text-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }} className="w-20 h-20 border-4 border-gorkhali-red border-t-transparent rounded-full mx-auto mb-4" />
        <p className="font-bebas text-2xl text-white">LOADING HISTORY</p>
      </motion.div>
    </div>
  );

  const pieData = selectedElection?.results?.map(r => ({ name: r.party, value: r.seats, fill: r.color })) || [];
  const turnoutData = getTurnoutTrendData(historicalData);

  // Party colors for consistent visualization
  const partyColors = {
    "CPN-UML": "#EF233C",
    "Nepali Congress": "#2A9D8F",
    "CPN-Maoist Centre": "#B91C1C",
    "CPN-Maoist": "#B91C1C",
    "UCPN-Maoist": "#B91C1C",
    "Rastriya Swatantra Party (RSP)": "#48CAE4",
    "RSP": "#48CAE4",
  };

  return (
    <div className="min-h-screen bg-night-count">
      <LiveTicker />
      
      {/* Hero */}
      <div className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Nepal" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-night-count via-night-count/90 to-night-count" />
        </div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-marigold/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-rsp-blue/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }}>
            <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 3, repeat: Infinity }}>
              <History className="mx-auto mb-6 text-marigold" size={64} />
            </motion.div>
            <h1 className="font-bebas text-6xl sm:text-7xl text-white tracking-wider mb-4" style={{ textShadow: "0 0 60px rgba(247,127,0,0.5)" }}>
              NEPAL'S DEMOCRATIC JOURNEY
            </h1>
            <p className="font-playfair text-xl text-gray-300 italic mb-2">"From monarchy to federal republic"</p>
            <p className="text-marigold font-bebas tracking-wider">2001 (BS 2058) — PRESENT</p>
          </motion.div>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="sticky top-0 z-40 bg-night-count/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <Tabs value={mainTab} onValueChange={setMainTab}>
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 bg-white/5">
              <TabsTrigger value="elections" className="font-bebas tracking-wider data-[state=active]:bg-marigold data-[state=active]:text-peace-blue">
                <Vote className="mr-2 h-4 w-4" /> ELECTIONS
              </TabsTrigger>
              <TabsTrigger value="pm-succession" className="font-bebas tracking-wider data-[state=active]:bg-marigold data-[state=active]:text-peace-blue">
                <Crown className="mr-2 h-4 w-4" /> PM LIST
              </TabsTrigger>
              <TabsTrigger value="timeline" className="font-bebas tracking-wider data-[state=active]:bg-marigold data-[state=active]:text-peace-blue">
                <Calendar className="mr-2 h-4 w-4" /> TIMELINE
              </TabsTrigger>
              <TabsTrigger value="provinces" className="font-bebas tracking-wider data-[state=active]:bg-marigold data-[state=active]:text-peace-blue">
                <MapPin className="mr-2 h-4 w-4" /> PROVINCES
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Tab Content */}
      <div className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* ELECTIONS TAB */}
          {mainTab === "elections" && (
            <div>
              {/* Turnout Trend */}
              <motion.section initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="mb-12">
                <div className="text-center mb-8">
                  <span className="text-marigold font-bebas tracking-widest text-sm">📈 VOTER PARTICIPATION</span>
                  <h2 className="font-bebas text-3xl text-white tracking-wider mt-2">TURNOUT TRENDS (2008-2022)</h2>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={turnoutData}>
                      <defs>
                        <linearGradient id="turnoutGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F7A000" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#F7A000" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="year" stroke="#9CA3AF" />
                      <YAxis stroke="#9CA3AF" domain={[50, 90]} tickFormatter={(v) => `${v}%`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#F7A000' }}
                        formatter={(value, name) => [`${value}%`, 'Turnout']}
                      />
                      <Area type="monotone" dataKey="turnout" stroke="#F7A000" fill="url(#turnoutGradient)" strokeWidth={3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.section>

              {/* Election Selector */}
              <div className="mb-8 max-w-md mx-auto">
                <Select value={selectedElection?.id} onValueChange={(v) => { setSelectedElection(historicalData.find(e => e.id === v)); setActiveTab("overview"); }}>
                  <SelectTrigger className="font-inter bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Select election" />
                  </SelectTrigger>
                  <SelectContent>
                    {historicalData.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.year} - {e.election_type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedElection && (
                <motion.div key={selectedElection.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  {/* Election Header */}
                  <div className="text-center mb-8">
                    <h2 className="font-bebas text-4xl text-white tracking-wider">{selectedElection.year} {selectedElection.election_type}</h2>
                    {selectedElection.description && (
                      <p className="font-playfair text-lg text-gray-400 italic mt-2">"{selectedElection.description}"</p>
                    )}
                    {selectedElection.notable && (
                      <p className="text-marigold mt-2 text-sm"><Award className="inline mr-1" size={16} />{selectedElection.notable}</p>
                    )}
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                    {[
                      { icon: Calendar, label: "Year", value: selectedElection.year, color: "#EF233C" },
                      { icon: Vote, label: "Total Seats", value: selectedElection.total_seats, color: "#2A9D8F" },
                      { icon: Users, label: "Voters", value: `${(selectedElection.total_voters / 1000000).toFixed(1)}M`, color: "#48CAE4" },
                      { icon: TrendingUp, label: "Turnout", value: `${selectedElection.turnout_percentage}%`, color: "#F7A000" }
                    ].map((s, i) => (
                      <motion.div 
                        key={s.label} 
                        initial={{ opacity: 0, y: 10 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        transition={{ delay: i * 0.1 }} 
                        className="bg-white/5 border border-white/10 p-4 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <s.icon style={{ color: s.color }} size={24} />
                          <div>
                            <p className="text-xs text-gray-500">{s.label}</p>
                            <p className="font-bebas text-2xl text-white">{s.value}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Results Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
                    <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto bg-white/5">
                      <TabsTrigger value="overview" className="font-bebas tracking-wider">TABLE</TabsTrigger>
                      <TabsTrigger value="bar" className="font-bebas tracking-wider">BAR CHART</TabsTrigger>
                      <TabsTrigger value="pie" className="font-bebas tracking-wider">PIE CHART</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-6">
                      <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-peace-blue/50">
                            <tr>
                              <th className="font-bebas tracking-wider text-left px-4 py-3 text-white">PARTY</th>
                              <th className="font-bebas tracking-wider text-center px-4 py-3 text-white">SEATS</th>
                              <th className="font-bebas tracking-wider text-center px-4 py-3 text-white">VOTE %</th>
                              <th className="font-bebas tracking-wider text-right px-4 py-3 text-white hidden sm:table-cell">SHARE</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedElection.results.map((r, i) => (
                              <tr key={r.party} className={i % 2 === 0 ? "bg-white/5" : "bg-white/[0.02]"}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
                                    <span className="font-inter text-sm text-white">{r.party}</span>
                                    {i === 0 && <Crown className="text-marigold flex-shrink-0" size={14} />}
                                  </div>
                                </td>
                                <td className="font-bebas text-xl text-center px-4 py-3 text-white">{r.seats}</td>
                                <td className="font-inter text-center px-4 py-3 text-gray-300">{r.vote_share}%</td>
                                <td className="px-4 py-3 hidden sm:table-cell">
                                  <div className="w-full bg-white/10 rounded-full h-2">
                                    <div 
                                      className="h-2 rounded-full transition-all duration-500" 
                                      style={{ width: `${(r.seats / selectedElection.total_seats) * 100}%`, backgroundColor: r.color }}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </TabsContent>

                    <TabsContent value="bar" className="mt-6">
                      <div className="bg-white/5 border border-white/10 p-6 rounded-lg">
                        <h3 className="font-bebas text-xl text-white mb-4 text-center">SEATS WON BY PARTY</h3>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={selectedElection.results} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis type="number" stroke="#9CA3AF" />
                            <YAxis type="category" dataKey="party" tick={{ fontSize: 11, fill: '#9CA3AF' }} width={150} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                              labelStyle={{ color: '#F7A000' }}
                            />
                            <Bar dataKey="seats" radius={[0, 8, 8, 0]}>
                              {selectedElection.results.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </TabsContent>

                    <TabsContent value="pie" className="mt-6">
                      <div className="bg-white/5 border border-white/10 p-6 rounded-lg">
                        <h3 className="font-bebas text-xl text-white mb-4 text-center">SEAT DISTRIBUTION</h3>
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie 
                              data={pieData} 
                              cx="50%" 
                              cy="50%" 
                              outerRadius={150} 
                              innerRadius={80} 
                              dataKey="value" 
                              label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                              labelLine={{ stroke: '#6B7280' }}
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                              formatter={(value, name) => [`${value} seats`, name]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        {/* Legend */}
                        <div className="flex flex-wrap justify-center gap-4 mt-4">
                          {selectedElection.results.slice(0, 5).map(r => (
                            <div key={r.party} className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color }} />
                              <span className="text-xs text-gray-400">{r.party}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </motion.div>
              )}
            </div>
          )}

          {/* PM SUCCESSION TAB */}
          {mainTab === "pm-succession" && (
            <div>
              <div className="text-center mb-12">
                <span className="text-marigold font-bebas tracking-widest text-sm">👤 LEADERSHIP</span>
                <h2 className="font-bebas text-4xl text-white tracking-wider mt-2">PRIME MINISTERS OF NEPAL</h2>
                <p className="text-gray-400 font-inter mt-2">Federal Democratic Republic Era (2006-Present)</p>
              </div>

              {/* PM Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg text-center">
                  <p className="font-bebas text-3xl text-marigold">{pmSuccession.length}</p>
                  <p className="text-gray-400 text-sm">Total PMs</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg text-center">
                  <p className="font-bebas text-3xl text-congress-green">{pmSuccession.filter(p => p.party.includes("Congress")).length}</p>
                  <p className="text-gray-400 text-sm">NC PMs</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg text-center">
                  <p className="font-bebas text-3xl text-communist-red">{pmSuccession.filter(p => p.party.includes("UML")).length}</p>
                  <p className="text-gray-400 text-sm">UML PMs</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-4 rounded-lg text-center">
                  <p className="font-bebas text-3xl text-[#B91C1C]">{pmSuccession.filter(p => p.party.includes("Maoist")).length}</p>
                  <p className="text-gray-400 text-sm">Maoist PMs</p>
                </div>
              </div>

              {/* PM Timeline */}
              <div className="max-w-3xl mx-auto">
                {pmSuccession.map((pm, index) => (
                  <PMCard 
                    key={pm.id} 
                    pm={pm} 
                    index={index} 
                    isLast={index === pmSuccession.length - 1} 
                  />
                ))}
              </div>
            </div>
          )}

          {/* TIMELINE TAB */}
          {mainTab === "timeline" && (
            <div>
              <div className="text-center mb-12">
                <span className="text-marigold font-bebas tracking-widest text-sm">📅 KEY EVENTS</span>
                <h2 className="font-bebas text-4xl text-white tracking-wider mt-2">POLITICAL TIMELINE</h2>
                <p className="text-gray-400 font-inter mt-2">From BS 2058 (2001 AD) to Present</p>
              </div>

              <div className="max-w-3xl mx-auto">
                {politicalTimeline.map((event, index) => (
                  <TimelineEvent key={`${event.year}-${event.event}`} event={event} index={index} />
                ))}
              </div>
            </div>
          )}

          {/* PROVINCES TAB */}
          {mainTab === "provinces" && (
            <div>
              <div className="text-center mb-12">
                <span className="text-marigold font-bebas tracking-widest text-sm">🏔️ FEDERAL STRUCTURE</span>
                <h2 className="font-bebas text-4xl text-white tracking-wider mt-2">7 PROVINCES OF NEPAL</h2>
                <p className="text-gray-400 font-inter mt-2">Established by the 2015 Constitution</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {provinces.map((p, i) => (
                  <motion.div 
                    key={p.id} 
                    initial={{ opacity: 0, y: 20 }} 
                    whileInView={{ opacity: 1, y: 0 }} 
                    transition={{ delay: i * 0.1 }} 
                    whileHover={{ y: -5, scale: 1.02 }} 
                    className="bg-white/5 border border-white/10 p-5 rounded-lg hover:bg-white/10 transition-all cursor-pointer"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 bg-marigold/20 rounded-full flex items-center justify-center">
                        <MapPin className="text-marigold" size={20} />
                      </div>
                      <div>
                        <h3 className="font-bebas text-xl text-white tracking-wider">{p.name}</h3>
                        <p className="font-inter text-sm text-gray-400">Capital: {p.capital}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white/5 rounded p-2 text-center">
                        <p className="font-bebas text-lg text-marigold">{p.federal_seats}</p>
                        <p className="text-gray-500 text-xs">Federal Seats</p>
                      </div>
                      <div className="bg-white/5 rounded p-2 text-center">
                        <p className="font-bebas text-lg text-rsp-blue">{p.provincial_seats}</p>
                        <p className="text-gray-500 text-xs">Provincial Seats</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Major Parties Legend */}
      <section className="py-12 px-4 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-bebas text-2xl text-white text-center mb-8 tracking-wider">MAJOR POLITICAL PARTIES</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 bg-white/5 p-4 rounded-lg">
              <UMLSunIcon size={32} className="text-communist-red" />
              <div>
                <p className="font-bebas text-white tracking-wider">CPN-UML</p>
                <p className="text-xs text-gray-500">Communist Party</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 p-4 rounded-lg">
              <CongressTreeIcon size={32} className="text-congress-green" />
              <div>
                <p className="font-bebas text-white tracking-wider">NEPALI CONGRESS</p>
                <p className="text-xs text-gray-500">Democratic Socialist</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 p-4 rounded-lg">
              <MaoistHammerIcon size={32} className="text-[#B91C1C]" />
              <div>
                <p className="font-bebas text-white tracking-wider">MAOIST CENTRE</p>
                <p className="text-xs text-gray-500">Communist Party</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 p-4 rounded-lg">
              <RSPBellIcon size={32} className="text-rsp-blue" />
              <div>
                <p className="font-bebas text-white tracking-wider">RSP</p>
                <p className="text-xs text-gray-500">Independent (New)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HistoryPage;
